import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  ColumnDef,
  flexRender,
  SortingState,
  RowSelectionState,
  PaginationState,
} from "@tanstack/react-table";
import { Image } from "react-bootstrap";
import cn from "classnames";
import { useState, useEffect, useRef, useMemo } from "react";
import CustomButton, {
  ButtonFont,
  ButtonSize,
  ButtonType,
} from "../../../../../../../component/common/CustomButton/CustomButton";
import { ASSESTS } from "../../../../../../../constant/assest";
import Pagination from "../../../../../../../component/UI/Pagination/Pagination";
import { format } from 'date-fns';
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../../../../redux/store";
import { fetchSubscribers } from "../../../../../../../redux/slices/newsletter/actions";
import TableSkeleton from "../../../../../../../component/common/TableSkeleton";
import { usePagination } from "../../../../../../../hooks/usePagination";

type Subscriber = {
  id: number;
  email: string;
  subs_from: string;
  is_subs: boolean;
  updated_at: string;
};

const NewslettersTable = ({ searchTerm = '' }: { searchTerm?: string }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { subscribers, totalCount, loading } = useSelector((state: RootState) => state.newsletter);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const prevSearchTerm = useRef(searchTerm);

  useEffect(() => {
    const searchTermChanged = prevSearchTerm.current !== searchTerm;

    if (searchTermChanged && pagination.pageIndex !== 0) {
      setPagination(old => ({ ...old, pageIndex: 0 }));
      return;
    }

    const params: { limit: number; email?: string; page?: number } = {
        limit: pagination.pageSize,
        email: searchTerm || undefined,
    };

    if (!searchTermChanged) {
        params.page = pagination.pageIndex + 1;
    }
    
    dispatch(fetchSubscribers(params));

    if(searchTermChanged) {
        prevSearchTerm.current = searchTerm;
    }
  }, [dispatch, pagination, searchTerm]);

  const columns= useMemo<ColumnDef<Subscriber>[]>(()=>  [
    {
        id: 'sno',
        header: 'S.No.',
        cell: ({ row }) => {
            return (pagination.pageIndex * pagination.pageSize) + row.index + 1;
        },
    },
    {
      header: "Email",
      accessorKey: "email",
    },
    {
      header: "Subscribed From",
      accessorKey: "subs_from",
    },
    {
      header: "Subscribed Date",
      accessorKey: "updated_at",
      cell: ({ getValue }) => {
        const date = getValue<string>();
        return date ? format(new Date(date), 'yyyy-MM-dd') : 'N/A';
      },
    },
  ],[pagination.pageIndex, pagination.pageSize]);

  const totalPages = Math.ceil(totalCount / pagination.pageSize);

  const table = useReactTable({
    data: subscribers || [],
    columns,
    state: { sorting, rowSelection, pagination },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: true,
    manualPagination: true,
    pageCount: totalPages,
  });


  const paginationGroup = usePagination({
    currentPageIndex: pagination.pageIndex,
    totalPages: totalPages,
  });

  const pageNumbersElements = paginationGroup.map((page, index) => {
    if (typeof page === 'string') {
      return <span key={`ellipsis-${index}`} className={'pageNumberBtn'} style={{cursor: 'default'}}>{page}</span>;
    }

    const pageIndex = page;
    return (
      <CustomButton
        key={pageIndex}
        onClick={() => table.setPageIndex(pageIndex)}
        disabled={pagination.pageIndex === pageIndex}
        btnType={
          pagination.pageIndex === pageIndex
            ? ButtonType.ACTIVENOSTYLE
            : ButtonType.NOSTYLE
        }
        btnSize={ButtonSize.NORMAL}
        btnFont={
          pagination.pageIndex === pageIndex
            ? ButtonFont.BOLD
            : ButtonFont.NORMAL
        }
        className={cn(
          'pageNumberBtn',
          pagination.pageIndex === pageIndex && 'activePageNumberBtn'
        )}
      >
        {pageIndex + 1}
      </CustomButton>
    );
  });

  return (
    <div className={'tableContainer'}>
      <div className={'tableWrapper'}>
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isActionColumn =
                    typeof header.column.columnDef.header === "string" &&
                    header.column.columnDef.header === "Action";
                  return (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      style={{
                        cursor: header.column.getCanSort()
                          ? "pointer"
                          : "default",
                      }}
                      className={cn(
                        header.column.columnDef.id &&
                          `th-${header.column.columnDef.id}`,
                        isActionColumn ? "text-center" : undefined
                      )}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {header.column.getCanSort() && (
                        <span className={cn("tableShorting")}>
                          <Image
                            src={
                              header.column.getIsSorted() === "asc"
                                ? ASSESTS.ASSENDING
                                : header.column.getIsSorted() === "desc"
                                ? ASSESTS.DECENDING
                                : ASSESTS.SHORT
                            }
                            alt="Short"
                          />
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading === 'pending' ? (
                <TableSkeleton  columns={ columns.length } rows= { pagination.pageSize }/>
            ) : table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                    const isActionColumn =
                        typeof cell.column.columnDef.header === "string" &&
                        cell.column.columnDef.header === "Action";

                    return (
                        <td
                        key={cell.id}
                        className={cn(
                            cell.column.columnDef.id &&
                            `td-${cell.column.columnDef.id}`,
                            isActionColumn ? "text-center" : undefined
                        )}
                        >
                        {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                        )}
                        </td>
                    );
                    })}
                </tr>
                ))
            ) : (
                <tr>
                  <td colSpan={columns.length} className={'noResultsFound'}>
                    No results found
                  </td>
                </tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination
        enteriesShows={`${
          totalCount > 0 ? pagination.pageIndex * pagination.pageSize + 1 : 0
        }-${Math.min(
          (pagination.pageIndex + 1) * pagination.pageSize,
          totalCount
        )} of ${totalCount} Entries`}
        pageNumbers= {[
          totalPages > 1 && (
            <CustomButton
              key="prev"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              btnType={ButtonType.NOSTYLE}
              className={'pageNumberBtn'}
            >
              Prev
            </CustomButton>
          ),
          ...pageNumbersElements,
          totalPages > 1 && (
            <CustomButton
              key="next"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              btnType={ButtonType.NOSTYLE}
              className={'pageNumberBtn'}
            >
              Next
            </CustomButton>
          )
        ].filter(Boolean)}
        dropDownOptions={[
          { value: "5", label: "5" },
          { value: "10", label: "10" },
          { value: "20", label: "20" },
          { value: "25", label: "25" },
        ]}
        dropdownOnChange={(value: any) => {
          setPagination((old) => ({
            ...old,
            pageSize: Number(value),
            pageIndex: 0,
          }));
        }}
        dropDownPlaceholder={pagination.pageSize.toString()}
      />
    </div>
  );
};

export default NewslettersTable;
